import { QUALITIES } from "@personnel/rules-leviathan";
import type { LeviathanCaptain, LeviathanDerived } from "@personnel/rules-leviathan";
import { REPUTATION_PATHS, rankTitle } from "@personnel/setting";

/**
 * Maps a captain onto the Captain's Log form fields.
 *
 * Kept separate from the pdf-lib call so it can be tested without loading a
 * 3 MB template, and so the field names live in exactly one place.
 *
 * Only creation data and the two Essence trackers are written. The form's
 * remaining fields -- the mission log, Stress and Command boxes, Battle Group
 * -- are campaign state, filled during play. The output is deliberately NOT flattened: the
 * Captain's Log is a living document, not a finished record.
 */

const QUALITY_NAMES = new Map(QUALITIES.map((q) => [q.id, q.name]));

/**
 * The Experience figures the form wants: XP is the total earned, XP Available
 * is what remains unspent. What that means depends on how the captain was
 * built, so each mode answers separately.
 */
function experienceFigures(
  captain: LeviathanCaptain,
  derived: LeviathanDerived,
): { total: string; available: string } {
  switch (captain.buildMode) {
    case "level":
      // Unspent Experience is forfeited, so the captain goes forward having
      // earned exactly what they spent. Writing the level's full allowance
      // would credit them with points they no longer have.
      return { total: String(derived.experienceCommitted), available: "0" };
    case "points":
      return captain.experiencePoints === null
        ? { total: "", available: "" }
        : {
            total: String(captain.experiencePoints),
            available: String(Math.max(0, derived.experienceRemaining ?? 0)),
          };
    case "cost":
      // Cost mode asserts no Experience at all; leave both for the player.
      return { total: "", available: "" };
  }
}

export function captainFieldValues(
  captain: LeviathanCaptain,
  derived: LeviathanDerived,
): Record<string, string> {
  const experience = experienceFigures(captain, derived);
  const qualities = captain.qualities
    .map((id) => QUALITY_NAMES.get(id))
    .filter((name): name is string => name !== undefined);

  return {
    Name: captain.name,
    Rank: rankTitle(captain.faction, derived.effectiveRank) ?? "",
    Rank_Attr: String(derived.effectiveRank),
    // The form carries the exact Cost; the rounded figure follows in brackets
    // so a table can read either without the record losing the real number.
    Cost: captain.showRoundedCost
      ? `${derived.totalCost} [${derived.roundedCost}]`
      : String(derived.totalCost),

    Tactical: String(captain.attributes.tactical),
    Execution: String(captain.attributes.execution),
    Tasking: String(captain.attributes.tasking),
    Resolve: String(captain.attributes.resolve),
    Reserve: String(captain.attributes.reserve),

    Qualities: qualities.join(", "),
    Essence: String(captain.essence),
    "Essence Used": String(captain.essenceUsed),

    Renown: String(captain.renown),
    Reputation: derived.reputation ?? "",
    "Reputation Path":
      captain.reputationPath === null ? "" : REPUTATION_PATHS[captain.reputationPath].name,

    XP: experience.total,
    "XP Available": experience.available,
  };
}

/** Template path for a faction. Each faction has its own artwork. */
export function templateFor(faction: NonNullable<LeviathanCaptain["faction"]>): string {
  return `/templates/log-${faction}.pdf`;
}
